import React, { useCallback, useRef, useState } from 'react';

import { Modal, ModalBody, ModalHeader } from '@change/ds-core-ui';
import { CheckCircleIcon } from '@change/ds-icons/CheckCircleIcon';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useShareEducationModalContext } from '../../shareEducationModal';
import type { OpenModalResult } from '../../shareEducationModal';

import { EducationModalBody } from './components/EducationModalBody';
import type { UseTiktokEducationModalProps } from './types';

export type OpenTiktokEducationModalFn = (options: UseTiktokEducationModalProps) => OpenModalResult;

/**
 * Returns [openModal, modal | null].
 *
 * When `ShareEducationModalProvider` is present in the tree the hook delegates
 * to the shared modal (modal === null, rendering is handled by ShareSlideUp).
 * Otherwise it falls back to a locally-managed modal instance.
 */
export function useEducationModal(): [openModal: OpenTiktokEducationModalFn, modal: React.ReactElement | null] {
	const openSharedModal = useShareEducationModalContext();
	const { translate } = useI18n();

	const heading = translate('fe.components.psf.tiktok.link_copied');

	// Local fallback
	const [localIsOpen, setLocalIsOpen] = useState(false);
	const [localOptions, setLocalOptions] = useState<UseTiktokEducationModalProps | null>(null);
	const localResolverRef = useRef<((result: { successfulShare: boolean } | undefined) => void) | null>(null);

	const localHandleClose = useCallback((result?: { successfulShare: boolean }) => {
		setLocalIsOpen(false);

		localResolverRef.current?.(result);
		localResolverRef.current = null;
	}, []);

	const localOpenModal = useCallback((options: UseTiktokEducationModalProps): OpenModalResult => {
		setLocalOptions(options);
		setLocalIsOpen(true);

		/**
		 * Need to resolve any prior pending promise so it doesn't hang
		 * in case users open the modal multiple times.
		 */
		localResolverRef.current?.(undefined);

		// oxlint-disable-next-line promise/avoid-new
		const promise = new Promise<{ successfulShare: boolean } | undefined>((resolve) => {
			localResolverRef.current = resolve;
		});

		return { waitForResult: async () => promise };
	}, []);

	// Shared-context path — builds TikTok content and delegates to shared modal.
	const sharedOpenModal = useCallback(
		(options: UseTiktokEducationModalProps): OpenModalResult => {
			const { onContinueAction } = options;

			return openSharedModal
				? openSharedModal((close) => ({
						heading,
						headingLeadingSlot: <CheckCircleIcon size="small" className="text-icon-success!" />,
						body: (
							<EducationModalBody
								onContinue={async () => {
									await onContinueAction();
									close({ successfulShare: true });
								}}
							/>
						),
					}))
				: { waitForResult: async () => undefined };
		},
		[openSharedModal, heading],
	);

	if (openSharedModal) {
		return [sharedOpenModal, null];
	}

	const localModal = (
		<Modal
			className="[&>div]:min-h-53"
			isOpen={localIsOpen}
			onClose={() => localHandleClose(undefined)}
			onOpenChange={(open) => {
				if (!open) localHandleClose(undefined);
			}}
			transformToSheet
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
		>
			<ModalHeader leadingSlot={<CheckCircleIcon size="small" className="text-icon-success!" />} title={heading} />
			<ModalBody className="border-stroke-weaker mt-4 border-t pt-0">
				{localOptions && (
					<EducationModalBody
						onContinue={async () => {
							await localOptions.onContinueAction();
							localHandleClose({ successfulShare: true });
						}}
					/>
				)}
			</ModalBody>
		</Modal>
	);

	return [localOpenModal, localModal];
}
